/**
 * GhostDeckViewer - Navigable slide-by-slide presentation of ghost deck output.
 *
 * Desktop: Two-column layout with thumbnail sidebar + slide content.
 * Mobile: Horizontal scroll strip above slide content with touch swipe support.
 * Keyboard: Container-scoped via tabIndex + onKeyDown (no global listeners).
 */

import { useState, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight, FileText, BarChart3, AlertTriangle, Target } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile, useIsTablet } from '@/hooks/useMediaQuery';
import { MarkdownContent } from '@/components/verdict/MarkdownContent';
import type { GhostDeckSlide } from '@/hooks/useStrategicStream';

const SLIDE_TYPE_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  executive_summary: { bg: 'bg-v-teal/20', text: 'text-v-teal-light', label: 'Executive Summary' },
  situation:         { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Situation' },
  analysis:          { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Analysis' },
  risk_assessment:   { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Risk Assessment' },
  recommendation:    { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Recommendation' },
  appendix:          { bg: 'bg-surface-light', text: 'text-text-secondary', label: 'Appendix' },
};

const SLIDE_TYPE_ICONS: Record<string, typeof FileText> = {
  executive_summary: FileText,
  situation: BarChart3,
  analysis: BarChart3,
  risk_assessment: AlertTriangle,
  recommendation: Target,
  appendix: FileText,
};

interface GhostDeckViewerProps {
  slides: GhostDeckSlide[];
  className?: string;
}

export function GhostDeckViewer({ slides, className }: GhostDeckViewerProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isSmallScreen = isMobile || isTablet;
  const containerRef = useRef<HTMLDivElement>(null);

  // Touch swipe state
  const touchStartX = useRef<number | null>(null);

  const goTo = useCallback((index: number) => {
    setCurrentIndex(Math.max(0, Math.min(index, slides.length - 1)));
  }, [slides.length]);

  const goPrev = useCallback(() => goTo(currentIndex - 1), [goTo, currentIndex]);
  const goNext = useCallback(() => goTo(currentIndex + 1), [goTo, currentIndex]);

  // Container-scoped keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
    if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
  }, [goPrev, goNext]);

  // Touch swipe handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) goNext();
      else goPrev();
    }
    touchStartX.current = null;
  }, [goNext, goPrev]);

  if (slides.length === 0) {
    return (
      <div className="text-center py-8 text-text-muted text-sm">
        No ghost deck slides were generated for this analysis.
      </div>
    );
  }

  const slide = slides[currentIndex];
  const typeStyle = SLIDE_TYPE_STYLES[slide.slideType] || SLIDE_TYPE_STYLES.analysis;
  const TypeIcon = SLIDE_TYPE_ICONS[slide.slideType] || FileText;

  // Thumbnail strip (shared between mobile scroll strip and desktop sidebar)
  const thumbnailList = slides.map((s, i) => {
    const style = SLIDE_TYPE_STYLES[s.slideType] || SLIDE_TYPE_STYLES.analysis;
    return (
      <button
        key={i}
        onClick={() => goTo(i)}
        className={cn(
          'flex-shrink-0 rounded-lg border p-2 text-left transition-all',
          isSmallScreen ? 'w-[100px]' : 'w-full',
          i === currentIndex
            ? 'border-v-teal-light/50 bg-v-teal/10'
            : 'border-border/30 bg-surface/50 hover:border-border-light/50 hover:bg-surface-light/50'
        )}
      >
        <div className="text-[10px] text-text-muted font-mono mb-0.5">Slide {i + 1}</div>
        <div className={cn(
          'font-medium truncate mb-1',
          isSmallScreen ? 'text-[10px]' : 'text-xs',
          i === currentIndex ? 'text-text-primary' : 'text-text-secondary'
        )}>
          {s.title}
        </div>
        <span className={cn('px-1.5 py-0.5 rounded text-[9px] uppercase font-medium', style.bg, style.text)}>
          {style.label}
        </span>
      </button>
    );
  });

  // Slide content
  const slideContent = (
    <div
      className="rounded-xl bg-surface/80 border border-border/50 p-6 min-h-[300px]"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Navigation header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={goPrev}
          disabled={currentIndex === 0}
          className={cn(
            'flex items-center gap-1 px-2 py-1 rounded-lg text-sm transition-colors',
            currentIndex === 0
              ? 'text-text-muted cursor-not-allowed'
              : 'text-text-secondary hover:text-text-primary hover:bg-surface-light/50'
          )}
        >
          <ChevronLeft className="w-4 h-4" />
          Prev
        </button>

        <div className="flex items-center gap-2 text-sm">
          <span className="text-text-muted">Slide {currentIndex + 1} of {slides.length}</span>
          <span className={cn('px-2 py-0.5 rounded text-xs font-medium', typeStyle.bg, typeStyle.text)}>
            <TypeIcon className="w-3 h-3 inline mr-1" />
            {typeStyle.label}
          </span>
        </div>

        <button
          onClick={goNext}
          disabled={currentIndex === slides.length - 1}
          className={cn(
            'flex items-center gap-1 px-2 py-1 rounded-lg text-sm transition-colors',
            currentIndex === slides.length - 1
              ? 'text-text-muted cursor-not-allowed'
              : 'text-text-secondary hover:text-text-primary hover:bg-surface-light/50'
          )}
        >
          Next
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Title */}
      <h3 className="text-lg font-bold text-text-primary mb-4 uppercase tracking-wide">
        {slide.title}
      </h3>

      {/* Headline insight */}
      {slide.headlineInsight && (
        <div className="rounded-lg bg-gradient-to-r from-v-teal/10 to-blue-500/5 border border-v-teal/20 p-4 mb-4">
          <p className="text-v-teal-light text-sm font-medium leading-relaxed">
            {slide.headlineInsight}
          </p>
        </div>
      )}

      {/* Key points */}
      {slide.keyPoints.length > 0 && (
        <div className="mb-4">
          <ul className="space-y-2">
            {slide.keyPoints.map((point, i) => (
              <li key={i} className="flex items-start gap-2 text-text-primary text-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-v-teal mt-2 flex-shrink-0" />
                <MarkdownContent content={point} className="flex-1 [&_p]:inline text-sm" inline />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Supporting evidence */}
      {slide.supportingEvidence.length > 0 && (
        <div className="pt-3 border-t border-border/30">
          <div className="flex flex-wrap gap-2">
            <span className="text-xs text-text-muted font-medium">Evidence:</span>
            {slide.supportingEvidence.map((ev, i) => (
              <span key={i} className="px-2 py-0.5 rounded bg-surface-light/70 text-text-secondary text-xs">
                {ev}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Workstream source */}
      {slide.workstreamSource && (
        <div className="mt-3 text-xs text-text-muted">
          Source: {slide.workstreamSource}
        </div>
      )}
    </div>
  );

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      className={cn('outline-none focus:ring-1 focus:ring-v-teal/30 rounded-xl', className)}
    >
      {isSmallScreen ? (
        // Mobile: Horizontal scroll strip above slide content
        <div className="space-y-3">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-zinc-700 scrollbar-track-transparent">
            {thumbnailList}
          </div>
          {slideContent}
        </div>
      ) : (
        // Desktop: Two-column layout with sidebar
        <div className="flex gap-4">
          <div className="w-[200px] flex-shrink-0 space-y-1.5 max-h-[500px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-zinc-700 scrollbar-track-transparent">
            {thumbnailList}
          </div>
          <div className="flex-1">
            {slideContent}
          </div>
        </div>
      )}
    </div>
  );
}
